import {
  ApplicationRef,
  ComponentFactoryResolver,
  ComponentRef,
  EmbeddedViewRef,
  Injectable,
  Injector,
  Optional,
  SkipSelf,
  Type,
} from '@angular/core';
import { take } from 'rxjs/operators';
import { QuestionComponent, QuestionComponentButton } from './components/question/question.component';
import { PromptDialogComponent, PromptDialogComponentResult } from './components/prompt/prompt.component';
import { ErrorsGridDialogComponent } from './components/errors-grid/errors-grid-modal.component';
import { StringHash } from '@app/shared/utils/utils-types';
import { ConfigServiceModel } from '@app/origami-api/services/config.service';
import { ErrorsInformationComponent } from './components/errors-information/errors-information.component';
import { OrigamiHttpResponse } from '@app/origami-api/services/base/service.base';
import { getErrorDescription } from '@app/origami-api/utils/error-utils';
import { DomSanitizer } from '@angular/platform-browser';
import { DialogsStoreService } from './dialogs-store.service';
import { SessionService } from '@app/session/session.service';
import { DialogWindowResponse } from '@app/origami-api/constants/dialog-window-response';
import { Subject } from 'rxjs';
import { TopicServiceModel } from '@app/origami-api/models/topic-service.model';
import { ButtonThemeColor } from '@progress/kendo-angular-buttons';
import { SIGNAL } from '@angular/core/primitives/signals';

export interface ShowErrorsDialogOptions {
  comment?: string;
  columns?: { name: string; title: string; width?: number }[];
  response?: OrigamiHttpResponse;
  data?: StringHash<string | number>[];
  title?: string;
  getErrorDescription?: (error: TopicServiceModel.TopicError) => string;
  getErrorObjectName?: (error: TopicServiceModel.TopicError) => string;
  closeAction?: () => void;
}
export interface StandardErrorDialogOptions {
  columns?: { name: string; title: string; width?: number }[];
  standardErrorDialogDataGetter?: (jobResult) => StringHash<string | number>[];
}

@Injectable()
export class DialogService {
  public sessionService: SessionService;
  private closeModalSubject = new Subject<void>();
  onClose$ = this.closeModalSubject.asObservable();

  constructor(
    @Optional() @SkipSelf() dialogService: DialogService,
    private appRef: ApplicationRef,
    private componentFactoryResolver: ComponentFactoryResolver,
    private injector: Injector,
    private sanitizer: DomSanitizer,
    private dialogsStoreService: DialogsStoreService,
  ) {
    // TODO: ugly hack but we can't just inject this dependency due to a cyclic reference
    if (dialogService && dialogService.sessionService) {
      this.sessionService = dialogService.sessionService;
    }
  }

  closeModal() {
    this.closeModalSubject.next();
  }

  close(dialog: any) {
    this.dialogsStoreService.remove(dialog);
  }

  closeAll() {
    this.dialogsStoreService.removeAll();
  }

  askYesNoQuestion(
    title: string,
    text: string,
    btnOkTitle?: string,
    btnCancelTitle?: string,
    hiddenCancel?: boolean,
    noIcon?: boolean,
    icon?: string,
    yesHandler?: (res: string) => Promise<{ close: boolean }>,
    warningMessage?: string,
    btnWarningStyle: boolean = false,
    width?: number,
    btnSuccessStyle: boolean = false,
    minWidth?: number,
  ): Promise<DialogWindowResponse.yes | DialogWindowResponse.no> {
    return <Promise<DialogWindowResponse.yes | DialogWindowResponse.no>>this.askQuestion(
      title,
      text,
      [
        {
          text: btnCancelTitle || tiStrings.app.misc.no,
          value: DialogWindowResponse.no,
          outline: true,
          closeAction: true,
          hidden: !!hiddenCancel,
        },
        {
          text: btnOkTitle || tiStrings.app.misc.yes,
          value: DialogWindowResponse.yes,
          primary: true,
          danger: btnWarningStyle,
          handler: yesHandler,
          green: btnSuccessStyle,
        },
      ],
      null,
      noIcon ? '' : icon || '',
      false,
      warningMessage,
      width,
      minWidth,
    );
  }

  /**
   * A proposal you can't refuse
   */
  askQuestionAsTheGodfather(title: string, text: string, btnTitle?: string): Promise<string> {
    return this.askQuestion(title, text, [
      { text: btnTitle || tiStrings.app.misc.ok, value: DialogWindowResponse.ok, primary: true, closeAction: true },
    ]);
  }

  // if id will be provided there will be chance to skip same question
  askQuestion(
    title: string,
    text: string,
    buttons: QuestionComponentButton[],
    id?: string,
    icon: string = 'fa fa-question-circle-o',
    ignoreNavigationClosing?: boolean,
    warningText?: string,
    width?: number,
    minWidth?: number,
    suppressDangerTitle?: boolean,
  ): Promise<string> {
    const cancelValue = 'cancel';
    if (id) {
      //const prevResult = localStorage.getItem(`QuestionsAnswer_${id}`);
      const prevResult = this.sessionService.user.preferences.getQuestionWindowAnswer(id);
      if (prevResult && prevResult !== cancelValue) {
        return Promise.resolve(prevResult);
      }
    }

    return this.showDialog(
      QuestionComponent,
      (instance: QuestionComponent, close: (res: string) => void) => {
        instance.id = id;
        instance.title = title;
        instance.text = text;
        instance.icon = icon;
        instance.buttons = buttons;
        instance.warningText = warningText;
        instance.width = width;
        instance.minWidth = minWidth;
        instance.suppressDangerTitle = !!suppressDangerTitle;
        instance.close.pipe(take(1)).subscribe((res) => {
          if (id && res.noMoreQuestions && res.value !== cancelValue) {
            //localStorage.setItem(`QuestionsAnswer_${id}`, res.value);
            this.sessionService.user.preferences.setQuestionWindowAnswer(
              this.sessionService.currentRoomId,
              id,
              res.value,
            );
          }
          close(res.value);
        });
      },
      false,
      ignoreNavigationClosing,
    );
  }

  showSuccessModal(title: string, text: string, btnTitle?: string) {
    return this.showDialog(QuestionComponent, (instance: QuestionComponent, close: (res: string) => void) => {
      instance.title = title;
      instance.text = text;
      instance.buttons = [
        { text: btnTitle || tiStrings.app.misc.ok, value: DialogWindowResponse.ok, primary: true, closeAction: true },
      ];
      instance.icon = 'fa fa-check-circle success';
      instance.close.pipe(take(1)).subscribe((res) => close(res.value));
    });
  }

  showErrorModal(title: string, text: string, buttons?: QuestionComponentButton[], denyClose = false) {
    return this.showDialog(QuestionComponent, (instance: QuestionComponent, close: (res: string) => void) => {
      instance.title = title;
      instance.text = text;
      instance.icon = '';
      instance.dangerTitle = true;
      if (denyClose) {
        instance.denyClose = denyClose;
      }
      instance.buttons = buttons || [
        { text: tiStrings.app.misc.cancel, value: DialogWindowResponse.close, outline: true, closeAction: true },
      ];
      instance.close.pipe(take(1)).subscribe((res) => close(res.value));
    });
  }

  showWarningModal(
    title: string,
    text: string,
    buttons?: QuestionComponentButton[],
    icon = 'fa fa-exclamation-circle warning',
    denyClose = false,
  ) {
    return this.showDialog(QuestionComponent, (instance: QuestionComponent, close: (res: string) => void) => {
      instance.title = title || tiStrings.app.misc.warning;
      instance.text = text;
      if (denyClose) {
        instance.denyClose = denyClose;
      }
      instance.buttons = buttons || [
        { text: tiStrings.app.misc.ok, value: DialogWindowResponse.ok, primary: true, closeAction: true },
      ];
      instance.icon = icon;
      instance.close.pipe(take(1)).subscribe((res) => close(res.value));
    });
  }

  showTimerModal(title: string, text: string, buttons?: QuestionComponentButton[], timeAmount?: number) {
    return this.showDialog(QuestionComponent, (instance: QuestionComponent, close: (res: string) => void) => {
      instance.title = title || tiStrings.app.misc.warning;
      instance.text = text;
      instance.buttons = [];
      instance.minWidth = 400;
      instance.autoCloseDelayTime = timeAmount || 10;
      //instance.icon = 'fa fa-exclamation-circle warning';
      instance.close.pipe(take(1)).subscribe((res) => close(res.value));
    });
  }

  askText(options: {
    title: string;
    inputType?: 'textarea' | 'input';
    label?: string;
    required?: boolean;
    btnOkTitle?: string;
    btnCancelTitle?: string;
    defaultValue?: string;
    text?: string;
    yesHandler?: (text: string) => Promise<{ close: boolean }>;
    cancelHandler?: () => Promise<{ close: boolean }>;
    textClass?: string;
    btnOkClass?: string;
    maxWidth?: number;
    maxLength?: number;
    themeColor?: ButtonThemeColor;
    modernBehavior?: boolean;
  }): Promise<PromptDialogComponentResult> {
    return this.showDialog(PromptDialogComponent, (instance: PromptDialogComponent, close: (res: any) => void) => {
      instance.title = options.title;
      if (options.defaultValue) {
        instance.value = options.defaultValue;
      }
      if (options.maxWidth) {
        instance.maxWidth = options.maxWidth;
      }

      instance.inputType = options.inputType || 'textarea';
      instance.label = options.label;
      instance.text = options.text && this.sanitizer.bypassSecurityTrustHtml(options.text);
      instance.required = options.required;
      instance.btnOkTitle = options.btnOkTitle;
      instance.btnCancelTitle = options.btnCancelTitle;
      instance.modernBehavior[SIGNAL].value = options?.modernBehavior;
      if (options.textClass) {
        instance.textClass = options.textClass;
      }
      instance.yesHandler = options.yesHandler;
      instance.cancelHandler = options.cancelHandler;
      instance.btnOkClass = options.btnOkClass;
      instance.themeColor = options?.themeColor;
      instance.close.pipe(take(1)).subscribe((res) => close(res));
    });
  }

  showErrors(options: ShowErrorsDialogOptions): Promise<null> {
    return this.showDialog(
      ErrorsGridDialogComponent,
      (dialog: ErrorsGridDialogComponent, close: (res: any) => void) => {
        dialog.title = options.title;

        dialog.comment = options.comment;
        if (!options.comment && options.response) {
          dialog.comment = getErrorDescription(options.response.ErrorCode);
        }

        dialog.columns = options.columns || [
          { name: 'errorCode', title: tiStrings.app.misc.errorCode, width: 30 },
          { name: 'errorDescription', title: tiStrings.app.misc.errorDescription, width: 100 },
        ];

        if (options.data) {
          dialog.data = options.data;
        } else if (options.response && options.response.Result) {
          let errorRecords;

          if (options.response.Result && options.response.Result.length) {
            errorRecords = options.response.Result;
          } else if (options.response.Result.ErrorRecords && options.response.Result.ErrorRecords.length) {
            errorRecords = options.response.Result.ErrorRecords;
          } else if (options.response.Result.ErrorInfo && options.response.Result.ErrorInfo.length) {
            errorRecords = options.response.Result.ErrorInfo;
          }

          if (errorRecords && errorRecords.length) {
            dialog.data = errorRecords.map((error) => {
              const description = options.getErrorDescription ? options.getErrorDescription(error) : null;
              const objectName = options.getErrorObjectName ? options.getErrorObjectName(error) : error.ObjectName;
              return {
                errorCode: error.ErrorCode,
                errorObjectName: objectName,
                errorDescription: description || getErrorDescription(error),
              };
            });
          }
        }

        dialog.data = dialog.data || null;
        dialog.close.subscribe((res) => {
          if (options?.closeAction) options.closeAction();
          close(res);
        });
      },
    );
  }

  showErrorsWithProcessing(options: {
    comment?: string;
    columns?: { name: string; title: string }[];
    response?: OrigamiHttpResponse;
    data?: StringHash<string | number>[];
    title?: string;
    processCallback?: (res: any) => void;
    processButtonTitle?: string;
    closeButtonTitle?: string;
    showCheckboxes?: boolean;
    processButtonDisabled?: boolean;
  }): Promise<{ cancel: boolean }> {
    return this.showDialog(
      ErrorsGridDialogComponent,
      (dialog: ErrorsGridDialogComponent, close: (res: { cancel: boolean }) => void) => {
        dialog.title = options.title;

        dialog.comment = options.comment;
        if (!options.comment && options.response) {
          dialog.comment = getErrorDescription(options.response.ErrorCode);
        }

        dialog.columns = options.columns || [
          { name: 'errorCode', title: tiStrings.app.misc.errorCode, width: 30 },
          { name: 'errorDescription', title: tiStrings.app.misc.errorDescription, width: 100 },
        ];

        if (options.processCallback) {
          dialog.processCallback = options.processCallback;
        }

        if (options.processButtonDisabled) {
          dialog.processButtonDisabled = true;
        }

        dialog.showCheckboxes = options.showCheckboxes === undefined ? true : options.showCheckboxes;
        dialog.showPostProcessButton = true;
        if (options.processButtonTitle) {
          dialog.processButtonTitle = options.processButtonTitle;
        }

        if (options.closeButtonTitle) {
          dialog.closeButtonTitle = options.closeButtonTitle;
        }

        if (options.data) {
          dialog.data = options.data;
        } else if (options.response && options.response.Result) {
          let errorRecords;

          if (options.response.Result && options.response.Result.length) {
            errorRecords = options.response.Result;
          } else if (options.response.Result.ErrorRecords && options.response.Result.ErrorRecords.length) {
            errorRecords = options.response.Result.ErrorRecords;
          } else if (options.response.Result.ErrorInfo && options.response.Result.ErrorInfo.length) {
            errorRecords = options.response.Result.ErrorInfo;
          }

          if (errorRecords && errorRecords.length) {
            dialog.data = errorRecords.map((error) => ({
              errorCode: error.ErrorCode,
              errorDescription: getErrorDescription(error),
            }));
          }
        }

        dialog.data = dialog.data || null;
        dialog.close.subscribe((res) => {
          if (res) {
            close(res);
          } else {
            close({ cancel: true });
          }
        });
        if (dialog.processCallback) {
          dialog.postProcess.subscribe((selectedIds) => {
            if ((selectedIds && selectedIds.length) || !dialog.showCheckboxes) {
              dialog.processCallback(selectedIds);
            } else {
              // dialog.processCallback(selectedIds);
              //close({cancel: true});
            }
          });
        }
      },
    );
  }

  showErrorsTree(data: StringHash<ConfigServiceModel.ErrorCategory[]>, text: string): Promise<null> {
    return this.showDialog(
      ErrorsInformationComponent,
      (instance: ErrorsInformationComponent, close: (res: any) => void) => {
        instance.data = data;
        instance.treeDataText = text;
        instance.close.pipe(take(1)).subscribe((res) => close(res));
      },
    );
  }

  showDialog<TDialog, TResult>(
    component: Type<TDialog>,
    updater: (instance: TDialog, close: (res: TResult) => void) => void,
    ignoreEscKey?: boolean,
    ignoreNavigationClosing?: boolean,
  ): Promise<TResult> {
    return new Promise<TResult>((resolve) => {
      const componentRef = this.createComponentInBody(component);
      this.dialogsStoreService.add({
        dialog: componentRef,
        destroy: () => this.removeComponentFromBody(componentRef),
        ignoreEscKey: ignoreEscKey,
        ignoreNavigationClosing: ignoreNavigationClosing,
      });

      updater(componentRef.instance, (res: TResult) => {
        this.close(componentRef.instance);
        resolve(res);
        componentRef.changeDetectorRef.detectChanges();
      });
      componentRef.changeDetectorRef.detectChanges();
    });
  }

  private createComponentInBody<T>(component: Type<T>, initializer?: (instance: T) => void): ComponentRef<T> {
    // 1. Create a component reference from the component
    const componentRef = this.componentFactoryResolver.resolveComponentFactory(component).create(this.injector);

    // 1.1 set component inputs
    initializer && initializer(componentRef.instance);

    // 2. Attach component to the appRef so that it's inside the ng component tree
    this.appRef.attachView(componentRef.hostView);

    // 3. Get DOM element from component
    const domElem = (componentRef.hostView as EmbeddedViewRef<any>).rootNodes[0] as HTMLElement;

    // 4. Append DOM element to the body
    document.body.appendChild(domElem);

    return componentRef;
  }

  private removeComponentFromBody<T>(componentRef: ComponentRef<T>): void {
    if (componentRef) {
      this.appRef.detachView(componentRef.hostView);
      componentRef.destroy();
    }
  }
}
